import { useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Typography
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/LogoutRounded';
import useAuth from '../../hooks/useAuth';
import CogIcon from '../../icons/Cog';
import UserIcon from '../../icons/User';
import ChevronDownIcon from '../../icons/ChevronDown';
import SettingsDrawer from '../SettingsDrawer';
import { authApi } from '../../api/authApi';
import { toneAlpha, lightVeil } from '../../theme/tones';

const AccountPopover = () => {
  const anchorRef = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  const { hostname } = window.location;
  const tenant = hostname.split('.')[0].toUpperCase();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      handleClose();
      // va enregistrer un log de déconnexion en bdd
      await authApi.logout(user.id, localStorage.getItem('accessToken'));
      await logout();
      navigate('/');
    } catch (err) {
      console.error(err);
      toast.error('Impossible de se déconnecter.');
    }
  };

  const fullName = `${user.firstname || ''} ${user.lastname || ''}`.trim();

  return (
    <>
      <SettingsDrawer
        openDrawer={openSettings}
        setOpenDrawer={setOpenSettings}
      />
      {/* Déclencheur : avatar + identité, regroupés dans une pastille
          cliquable plutôt qu'un avatar nu flottant dans la barre. */}
      <Box
        component={ButtonBase}
        onClick={handleOpen}
        ref={anchorRef}
        sx={{
          alignItems: 'center',
          display: 'flex',
          gap: 1,
          pl: 0.5,
          pr: { xs: 0.5, lg: 1.25 },
          py: 0.5,
          borderRadius: '999px',
          border: '1px solid',
          borderColor: 'divider',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: 'action.hover',
            borderColor: 'secondary.main'
          }
        }}
      >
        <Avatar
          src={`data:image/png;base64,${user.photo}`}
          sx={{
            height: 32,
            width: 32,
            border: 'none'
          }}
        />
        <Box
          sx={{
            display: { xs: 'none', lg: 'block' },
            textAlign: 'left',
            maxWidth: 150,
            overflow: 'hidden'
          }}
        >
          <Typography
            variant="subtitle2"
            color="textPrimary"
            noWrap
            sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}
          >
            {fullName}
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            noWrap
            sx={{ display: 'block', fontSize: '0.7rem', lineHeight: 1.3 }}
          >
            {user.functions}
          </Typography>
        </Box>
        <ChevronDownIcon
          fontSize="small"
          sx={{
            display: { xs: 'none', lg: 'block' },
            color: 'text.secondary',
            fontSize: '1rem'
          }}
        />
      </Box>

      <Popover
        anchorEl={anchorRef.current}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom'
        }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top'
        }}
        keepMounted
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: { width: 268, mt: 1.5, overflow: 'hidden' }
        }}
      >
        {/* En-tête identitaire, sur fond teinté pour se détacher du menu. */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            background: (theme) => (theme.palette.mode === 'light'
              ? `linear-gradient(135deg, ${toneAlpha('brand', 0.05)} 0%, ${toneAlpha('blue', 0.07)} 100%)`
              : `linear-gradient(135deg, ${lightVeil(0.04)} 0%, ${toneAlpha('blue', 0.08)} 100%)`)
          }}
        >
          <Avatar
            src={`data:image/png;base64,${user.photo}`}
            sx={{ height: 44, width: 44 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography color="textPrimary" variant="subtitle2" noWrap>
              {fullName}
            </Typography>
            <Typography color="textSecondary" variant="caption" noWrap sx={{ display: 'block' }}>
              {user.functions}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'inline-block',
                mt: 0.5,
                px: 0.75,
                py: 0.1,
                borderRadius: '999px',
                fontWeight: 700,
                fontSize: '0.62rem',
                letterSpacing: '0.08em',
                color: 'secondary.main',
                backgroundColor: 'action.selected'
              }}
            >
              {tenant}
            </Typography>
          </Box>
        </Box>
        <Divider />

        <Box sx={{ p: 0.75 }}>
          <MenuItem
            component={RouterLink}
            to="/dashboard/account"
            onClick={handleClose}
          >
            <ListItemIcon>
              <UserIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={(
                <Typography
                  color="textPrimary"
                  variant="subtitle2"
                >
                  Profil
                </Typography>
              )}
            />
          </MenuItem>
          <MenuItem>
            <ListItemIcon>
              <CogIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={(
                <Typography
                  color="textPrimary"
                  variant="subtitle2"
                  onClick={() => { setOpenSettings(true); handleClose(); }}
                >
                  Paramètres
                </Typography>
              )}
            />
          </MenuItem>
        </Box>
        <Divider />
        <Box sx={{ p: 1.5 }}>
          <Button
            color="primary"
            fullWidth
            onClick={handleLogout}
            variant="outlined"
            startIcon={<LogoutIcon fontSize="small" />}
          >
            Déconnexion
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default AccountPopover;
